import {
  Box,
  CalculatedField,
  ComputedFieldProps,
  Form,
  FormSection,
  Loader,
} from "@dbn/omnichannel-design-system-g4p";
import * as api from "../api";
import { eqExpensesCalculatorData, ExpensesCalculatorData } from "../api";
import { option, taskEither } from "fp-ts";
import {
  LocaleKey,
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatMoneyAmountValue,
} from "../../../../intl";
import { RegularExpensesForm } from "./RegularExpensesForm";
import { NecessaryExpensesForm } from "./NecessaryExpensesForm";
import { useExpensesCalculatorContext } from "./ExpensesCalculatorProvider";
import { useCommand } from "../../../../useAPI";
import { flow, pipe } from "fp-ts/function";
import { unsafeNonNegativeInteger } from "../../../../globalDomain";
import { useTenantCurrency } from "../../../../Common/useTenantCurrency";
import { useState } from "react";
import { useUpdateEffect } from "react-use";

type Props = {
  onCancel: () => unknown;
  onSaved: () => unknown;
  data: ExpensesCalculatorData;
};

export type ComputedFieldPropsExtended<V> = ComputedFieldProps<V> & {
  translationKey: LocaleKey;
};

export function ExpensesCalculator(props: Props) {
  const formatMessage = useFormatMessage();
  const formatMoneyAmountValue = useFormatMoneyAmountValue(
    unsafeNonNegativeInteger(0)
  );
  const tenantCurrency = useTenantCurrency();

  const saveExpenseCalculatorData = useCommand(api.saveExpenseCalculatorData);

  const { necessarySum, calculatorData } = useExpensesCalculatorContext();

  const [isLoading, setIsLoading] = useState(false);

  useUpdateEffect(() => {
    if (isLoading) {
      if (eqExpensesCalculatorData.equals(calculatorData, props.data)) {
        props.onCancel();
      } else {
        pipe(
          saveExpenseCalculatorData({
            expensesCalculator: {
              ...calculatorData,
              totalRegularMonthlyExpensesNoLiabilities: option.some({
                amount: necessarySum,
                currency: tenantCurrency,
              }),
            },
          }),
          taskEither.bimap(
            () => setIsLoading(false),
            flow(
              () => setIsLoading(false),
              () => {
                props.onSaved();
              }
            )
          )
        )();
      }
    }
  }, [isLoading]);

  return isLoading ? (
    <Loader
      ariaLabelMessage={formatMessage(
        "generic.loading.alt" as RuntimeLocaleKey
      )}
    />
  ) : (
    <Box grow shrink style={{ margin: "6px" }}>
      <Form
        shrink
        grow
        submitButton={{
          action: taskEither.fromIO(() => {
            let el = document.querySelector(":focus");

            (el as HTMLElement)?.blur();

            setIsLoading(true);
          }),
          label: formatMessage(
            "StandardLoan.ExpensesConfirmation.Calculator.Form.save"
          ),
        }}
        cancelButton={{
          action: props.onCancel,
          label: formatMessage(
            "StandardLoan.ExpensesConfirmation.Calculator.Form.close"
          ),
        }}
      >
        <FormSection>
          <NecessaryExpensesForm
            necessaryExpensesData={props.data.necessaryExpenses}
          />
          <RegularExpensesForm
            regularExpensesData={props.data.regularExpenses}
          />
        </FormSection>
        <CalculatedField
          type={"regular"}
          value={formatMoneyAmountValue({
            amount: necessarySum,
            currency: tenantCurrency,
          })}
          label={formatMessage(
            "StandardLoan.ExpensesConfirmation.Calculator.Form.total"
          )}
          error={option.none}
        />
      </Form>
    </Box>
  );
}
